import { pickBy } from "lodash";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants.ts";
import {
  downloadFile,
  openLink,
  requestWriteAccess,
  retrieveLaunchParams,
} from "@telegram-apps/sdk-react";
import { captureTelegramError } from "utils/sentry.ts";
import { LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY } from "common/constants.ts";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const parseInitDataHash = (rawInitData: string): string | undefined => {
  try {
    const params = new URLSearchParams(rawInitData);
    return params.get("hash") ?? undefined;
  } catch {
    return undefined;
  }
};

export const loadSavedInitDataHashes = (): Record<string, number> => {
  try {
    const raw = localStorage?.getItem(LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

export const saveSavedInitDataHashes = (hashes: Record<string, number>) => {
  localStorage?.setItem(
    LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY,
    JSON.stringify(hashes)
  );
};

export const getCleanOldInitDataHashes = () => {
  const saved = loadSavedInitDataHashes();
  const now = Date.now();
  return pickBy(saved, (timestamp) => now - timestamp < ONE_DAY_MS);
};

export const safeRetrieveLaunchParams = () => {
  try {
    return retrieveLaunchParams();
  } catch {}
};

/** Whether Telegram took the link (it can't open one in some clients). */
export const telegramOpenLink = (url?: string): boolean => {
  try {
    if (url && IS_TELEGRAM_WEB_APP && openLink.isAvailable()) {
      openLink(url);
      return true;
    }
  } catch (error) {
    const launchParams = IS_TELEGRAM_WEB_APP
      ? safeRetrieveLaunchParams()
      : null;
    captureTelegramError(error, {
      method: "openLink",
      location: "telegramOpenLink",
      additionalData: { url, launchParams },
    });
  }
  return false;
};

export const telegramDownloadFile = (url?: string, fileName?: string) => {
  const launchParams = IS_TELEGRAM_WEB_APP ? safeRetrieveLaunchParams() : null;
  try {
    if (url && fileName && IS_TELEGRAM_WEB_APP && downloadFile.isAvailable()) {
      downloadFile(url, fileName).catch((error) => {
        captureTelegramError(error, {
          method: "downloadFile",
          location: "telegramDownloadFile - promise rejection",
          additionalData: { url, fileName, launchParams },
        });
      });
    }
  } catch (error) {
    captureTelegramError(error, {
      method: "downloadFile",
      location: "telegramDownloadFile",
      additionalData: { url, fileName, launchParams },
    });
  }
};

export const telegramRequestWriteAccess = (
  callback?: (allowSendingPm?: boolean) => void
) => {
  const launchParams = IS_TELEGRAM_WEB_APP ? safeRetrieveLaunchParams() : null;
  try {
    if (IS_TELEGRAM_WEB_APP && requestWriteAccess.isAvailable()) {
      requestWriteAccess()
        .then((result) => {
          callback?.(result === "allowed");
        })
        .catch((error) => {
          captureTelegramError(error, {
            method: "requestWriteAccess",
            location: "telegramRequestWriteAccess - promise rejection",
            additionalData: { launchParams },
          });
        });
    }
  } catch (error) {
    captureTelegramError(error, {
      method: "requestWriteAccess",
      location: "telegramRequestWriteAccess",
      additionalData: { launchParams },
    });
  }
};

const CHANNEL_ENTRY_KEY = "cornix_channel_entry";

/**
 * This session started from a channel post's button (View Signal, Set Auto
 * Trading). A new user stays in onboarding, but this session keeps the
 * channel flow: the platform (no onboarding redirects) with its quick
 * onboarding, until they create a bot, which ends onboarding.
 */
export const markChannelEntry = (path: string): void => {
  try {
    sessionStorage.setItem(CHANNEL_ENTRY_KEY, path);
  } catch {
    return;
  }
};

/** The button's path; it outlives the start parameter in the URL. */
export const getChannelEntryPath = (): string | undefined => {
  try {
    return sessionStorage.getItem(CHANNEL_ENTRY_KEY) || undefined;
  } catch {
    return undefined;
  }
};

export const isChannelEntry = (): boolean => !!getChannelEntryPath();
